import { DurationString } from '@chronosphereio/core';
import { createContext, ReactNode, useContext, useEffect, useRef } from 'react';
import { createStore, StoreApi } from 'zustand';
import { devtools } from 'zustand/middleware';
import { immer } from 'zustand/middleware/immer';
import { shallow } from 'zustand/shallow';
import { useStoreWithEqualityFn } from 'zustand/traditional';
import { DashboardMetadata, DashboardResource, DashboardTimeDefinition, Display } from '@/core';
import { usePlugin, usePluginRegistry, useVizFrameworkConfig } from '../../../plugin-system';
import { CleanUpLayoutSlice, createCleanUpLayoutSlice } from './clean-up-layout-slice';
import { createPanelDefinition } from './common';
import { createDeletePanelGroupSlice, DeletePanelGroupSlice } from './delete-panel-group-slice';
import { createDeletePanelSlice, DeletePanelSlice } from './delete-panel-slice';
import { createDiscardChangesDialogSlice, DiscardChangesConfirmationDialogSlice } from './discard-changes-dialog-slice';
import { createDuplicatePanelSlice, DuplicatePanelSlice } from './duplicate-panel-slice';
import { createEditJsonDialogSlice, EditJsonDialogSlice } from './edit-json-dialog-slice';
import { createFocusSlice, FocusSlice } from './focus-slice';
import { createHistorySlice, HistorySlice } from './history-slice';
import { createPanelEditorSlice, PanelEditorSlice } from './panel-editor-slice';
import { createPanelGroupEditorSlice, PanelGroupEditorSlice } from './panel-group-editor-slice';
import { convertLayoutsToPanelGroups, createPanelGroupSlice, PanelGroupSlice } from './panel-group-slice';
import { createPanelSlice, PanelSlice } from './panel-slice';
import { createPastePanelSlice, PastePanelSlice } from './paste-panel-slice';
import { createQuickActionsSlice, QuickActionsSlice } from './quick-actions-slice';
import { createSaveChangesDialogSlice, SaveChangesConfirmationDialogSlice } from './save-changes-dialog-slice';
import { createSettingsDrawerSlice, SettingsDrawerSlice } from './settings-drawer-slice';

export interface DashboardStoreState
  extends PanelGroupSlice,
    PanelSlice,
    PanelGroupEditorSlice,
    DeletePanelGroupSlice,
    PanelEditorSlice,
    DeletePanelSlice,
    DiscardChangesConfirmationDialogSlice,
    DuplicatePanelSlice,
    PastePanelSlice,
    QuickActionsSlice,
    HistorySlice,
    FocusSlice,
    EditJsonDialogSlice,
    SettingsDrawerSlice,
    CleanUpLayoutSlice,
    SaveChangesConfirmationDialogSlice {
  isEditMode: boolean;
  setEditMode: (isEditMode: boolean) => void;
  setDashboard: (dashboard: DashboardResource) => void;
  metadata: DashboardMetadata;
  setMetadata: (metadata: DashboardMetadata) => void;
  duration: DurationString | undefined;
  time?: DashboardTimeDefinition;
  display?: Display;
  specVersion: string;
}

export interface DashboardStoreProps {
  dashboardResource: DashboardResource;
  metadata: DashboardMetadata;
  isEditMode?: boolean;
  enablePastePanelDefinition?: boolean;
}

type OnCreateAnnotationArgs = {
  timestamp: number;
  labels?: {
    [key: string]: string;
  };
};

export interface DashboardProviderProps {
  initialState: DashboardStoreProps;
  children?: ReactNode;
  onCreateAnnotation?: (args: OnCreateAnnotationArgs) => void;
}

export type DashboardContextType = {
  store: StoreApi<DashboardStoreState>;
  onCreateAnnotation?: (args: OnCreateAnnotationArgs) => void;
};

export const DashboardContext = createContext<DashboardContextType | undefined>(undefined);

export function useDashboardStore<T>(selector: (state: DashboardStoreState) => T) {
  const ctx = useContext(DashboardContext);
  if (ctx === undefined) {
    throw new Error('No DashboardContext found. Did you forget a Provider?');
  }
  return useStoreWithEqualityFn(ctx.store, selector, shallow);
}

export function useDashboardAnnotationConfig() {
  const ctx = useContext(DashboardContext);
  return ctx?.onCreateAnnotation;
}

export function DashboardProvider(props: DashboardProviderProps) {
  const { onCreateAnnotation } = props;
  const storeRef = useRef<StoreApi<DashboardStoreState>>();
  if (!storeRef.current) {
    storeRef.current = initStore(props);
  }
  const store = storeRef.current;

  // load plugin to retrieve initial spec if default panel kind is defined
  const { defaultPluginKinds } = usePluginRegistry();
  const defaultPanelKind = defaultPluginKinds?.['Panel'] ?? '';
  const { data: plugin } = usePlugin('Panel', defaultPanelKind);

  const config = useVizFrameworkConfig();

  useEffect(() => {
    if (plugin === undefined) return;
    const defaultPanelSpec = plugin.createInitialOptions({ config });
    // set default panel kind, spec, and queries for add panel editor
    store.setState({
      initialValues: {
        panelDefinition: createPanelDefinition(defaultPanelKind, defaultPanelSpec),
      },
    });
  }, [plugin, store, defaultPanelKind, config]);

  return (
    <DashboardContext.Provider value={{ store: store as StoreApi<DashboardStoreState>, onCreateAnnotation }}>
      {props.children}
    </DashboardContext.Provider>
  );
}

function initStore(props: DashboardProviderProps) {
  const {
    initialState: { dashboardResource, metadata, isEditMode, enablePastePanelDefinition },
  } = props;

  const {
    spec: { display, duration, time },
    spec_version,
  } = dashboardResource;

  let {
    spec: { layouts, panels },
  } = dashboardResource;

  // Set fallbacks in case the frontend is used with a non-Perses backend
  layouts = layouts ?? [];
  panels = panels ?? {};

  const store = createStore<DashboardStoreState>()(
    immer(
      devtools(
        (...args) => {
          const [set] = args;
          return {
            /* Groups */
            ...createPanelGroupSlice(layouts)(...args),
            ...createPanelGroupEditorSlice(...args),
            ...createDeletePanelGroupSlice(...args),
            /* Panels */
            ...createPanelSlice(panels)(...args),
            ...createPanelEditorSlice()(...args),
            ...createDeletePanelSlice()(...args),
            ...createDuplicatePanelSlice()(...args),
            ...createPastePanelSlice()(...args),
            /* Focus - only update when paste as json is enabled */
            ...createFocusSlice(enablePastePanelDefinition)(...args),
            /* General */
            ...createDiscardChangesDialogSlice(...args),
            ...createEditJsonDialogSlice(...args),
            ...createSaveChangesDialogSlice(...args),
            ...createSettingsDrawerSlice(...args),
            ...createCleanUpLayoutSlice(...args),
            ...createQuickActionsSlice()(...args),
            ...createHistorySlice()(...args),
            metadata,
            setMetadata: (metadata: DashboardMetadata) => set({ metadata }, false, '[Dashboard] setMetadata'),
            display,
            specVersion: spec_version,
            duration,
            time,
            isEditMode: !!isEditMode,
            setEditMode: (isEditMode: boolean) => set({ isEditMode }, false, '[Dashboard] setEditMode'),
            setDashboard: ({ spec: { display, panels = {}, layouts = [], duration, time } }) => {
              set(
                (state) => {
                  state.display = display;
                  state.panels = panels;
                  const { panelGroups, panelGroupOrder } = convertLayoutsToPanelGroups(layouts);
                  state.panelGroups = panelGroups;
                  state.panelGroupOrder = panelGroupOrder;
                  state.duration = duration;
                  state.time = time;
                },
                false,
                '[Dashboard] setDashboard'
              );
            },
          };
        },
        { name: 'DashboardStore' }
      )
    )
  );

  return store;
}
